import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;

    // Try to parse JSON error response
    let errorMessage = text;
    let errorDetails: any = null;
    
    try {
      const json = JSON.parse(text);
      errorDetails = json;
      
      if (json.message) {
        errorMessage = json.message;

        // If there are validation errors, append them to the message
        if (json.errors && Array.isArray(json.errors) && json.errors.length > 0) {
          const validationMessages = json.errors
            .map((err: any) => err.message || err.error)
            .filter(Boolean)
            .join('; ');

          if (validationMessages) {
            errorMessage = `${errorMessage}: ${validationMessages}`;
          }
        }
      } else if (json.error) {
        errorMessage = json.error;
      }
    } catch {
      // If JSON parsing fails, use the raw text
    }

    // Handle permission errors (403) with a toast — but skip tier-gated 403s
    // (those are handled silently by FeatureGate hiding the UI)
    if (res.status === 403 && !errorDetails?.requiredTier) {
      toast({
        title: "Permission Denied",
        description: errorMessage,
        variant: "destructive",
      });
    }

    // Create error with full details attached
    const error: any = new Error(errorMessage);
    error.details = errorDetails;
    error.statusCode = res.status;
    
    throw error;
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  // Longer timeout for AI analysis endpoints, 30s default for everything else
  const isLongRunning = url.includes("/analyze");
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), isLongRunning ? 180000 : 30000);

  try {
    const res = await fetch(url, {
      method,
      headers: data ? { "Content-Type": "application/json" } : {},
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    await throwIfResNotOk(res);
    return res;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("Request timeout - the server took too long to respond");
    }
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
type ForbiddenBehavior = "returnNull" | "throw";

export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
  on403?: ForbiddenBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior, on403: forbiddenBehavior = "throw" }) =>
  async ({ queryKey }) => {
    let url = queryKey[0] as string;

    // Handle query parameters if provided in queryKey[1]
    if (queryKey[1] && typeof queryKey[1] === 'object') {
      const params = queryKey[1] as Record<string, any>;
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && key !== 'disabled') {
          queryParams.append(key, String(value));
        }
      });

      const queryString = queryParams.toString();
      if (queryString) {
        url = `${url}?${queryString}`;
      }
    }

    const res = await fetch(url, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    // Handle 403 permission errors
    if (res.status === 403) {
      const text = (await res.text()) || res.statusText;
      let message = "You don't have permission to view this content";
      let isTierGated = false;
      try {
        const json = JSON.parse(text);
        if (json.message) {
          message = json.message;
        }
        // Tier-gated 403s include requiredTier — suppress toast for those
        if (json.requiredTier) {
          isTierGated = true;
        }
      } catch {
        // Use default message
      }

      if (!isTierGated) {
        toast({
          title: "Permission Denied",
          description: message,
          variant: "destructive",
        });
      }

      if (forbiddenBehavior === "returnNull" || isTierGated) {
        return null;
      }
      throw new Error(message);
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
